import React, { useState } from 'react';
import { LogIn, Mail, Lock, Eye, EyeOff, AlertCircle, UserPlus, ShieldCheck, KeyRound } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from 'axios';

function Login({ onLogin }) {
  const [email, setEmail]               = useState('');
  const [emailConfirm, setEmailConfirm] = useState('');
  const [password, setPassword]         = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe]     = useState(false);
  const [loading, setLoading]           = useState(false);
  const [isFirstSetup, setIsFirstSetup] = useState(false);
  const [checkingSetup, setCheckingSetup] = useState(true);
  const [error, setError]               = useState('');

  // 2FA step
  const [step, setStep]               = useState('credentials'); // 'credentials' | 'totp'
  const [preAuthToken, setPreAuthToken] = useState('');
  const [totpCode, setTotpCode]         = useState('');

  React.useEffect(() => { checkFirstSetup(); }, []);

  const checkFirstSetup = async () => {
    try {
      const response = await axios.get('/api/auth/check-setup');
      setIsFirstSetup(response.data.needsSetup);
    } catch {
      setIsFirstSetup(true);
    } finally {
      setCheckingSetup(false);
    }
  };

  // ── Step 1: email + password ───────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isFirstSetup) {
        if (email !== emailConfirm) {
          setError('Los correos no coinciden');
          toast.error('Los correos no coinciden');
          return;
        }
        if (password.length < 8) {
          setError('La contraseña debe tener al menos 8 caracteres');
          toast.error('La contraseña debe tener al menos 8 caracteres');
          return;
        }
        const response = await axios.post('/api/auth/setup', { email, password });
        toast.success('¡Cuenta creada exitosamente!');
        onLogin(response.data.token, response.data.user, rememberMe);
      } else {
        const response = await axios.post('/api/auth/login', { email, password });

        if (response.data.requires_2fa) {
          // Password OK — 2FA required
          setPreAuthToken(response.data.pre_auth_token);
          setStep('totp');
          setError('');
        } else {
          toast.success('¡Bienvenido!');
          onLogin(response.data.token, response.data.user, rememberMe);
        }
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Error al iniciar sesión';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: TOTP code ──────────────────────────────────────────────────────

  const handleTotpSubmit = async (e) => {
    e.preventDefault();
    if (totpCode.length !== 6) {
      setError('El código debe tener 6 dígitos');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await axios.post('/api/auth/2fa/validate', {
        pre_auth_token: preAuthToken,
        totp_code: totpCode,
      });
      toast.success('¡Bienvenido!');
      onLogin(response.data.token, response.data.user, rememberMe);
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Código incorrecto';
      setError(errorMsg);
      toast.error(errorMsg);
      setTotpCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleTotpInput = (val) => {
    // Accept only digits, max 6
    const digits = val.replace(/\D/g, '').slice(0, 6);
    setTotpCode(digits);
  };

  const handleBackToCredentials = () => {
    setStep('credentials');
    setPreAuthToken('');
    setTotpCode('');
    setError('');
    setPassword('');
  };

  // ── Loading skeleton ───────────────────────────────────────────────────────

  if (checkingSetup) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#0f1117' }}>
        <div className="loading-premium"></div>
      </div>
    );
  }

  // ── Shared styles ──────────────────────────────────────────────────────────

  const styles = `
    @keyframes loginSpin {
      to { transform: rotate(360deg); }
    }
    @keyframes loginFadeIn {
      from { opacity: 0; transform: translateY(16px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .login-input {
      width: 100%;
      padding: 14px 16px 14px 46px;
      background: #1a1e2e;
      border: 1px solid #252837;
      border-radius: 12px;
      color: #f3f4f6;
      font-size: 15px;
      outline: none;
      box-sizing: border-box;
      transition: border-color 0.2s, box-shadow 0.2s;
      font-family: inherit;
    }
    .login-input::placeholder { color: #4b5563; }
    .login-input:focus {
      border-color: #6366f1;
      box-shadow: 0 0 0 3px rgba(99,102,241,0.18);
    }
    .login-input-pass { padding-right: 46px; }
    .totp-input {
      width: 100%;
      padding: 18px 16px;
      background: #1a1e2e;
      border: 2px solid #252837;
      border-radius: 14px;
      color: #f3f4f6;
      font-size: 28px;
      font-weight: 700;
      text-align: center;
      letter-spacing: 0.35em;
      outline: none;
      box-sizing: border-box;
      transition: border-color 0.2s, box-shadow 0.2s;
      font-family: 'Courier New', monospace;
    }
    .totp-input::placeholder { color: #374151; letter-spacing: 0.2em; font-size: 22px; font-weight: 400; }
    .totp-input:focus {
      border-color: #6366f1;
      box-shadow: 0 0 0 3px rgba(99,102,241,0.18);
    }
    .login-submit-btn {
      width: 100%;
      padding: 15px;
      background: linear-gradient(135deg, #6366f1 0%, #7c3aed 100%);
      border: none;
      border-radius: 12px;
      color: white;
      font-size: 15px;
      font-weight: 600;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      transition: opacity 0.2s, transform 0.1s;
      letter-spacing: 0.01em;
      font-family: inherit;
    }
    .login-submit-btn:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
    .login-submit-btn:active:not(:disabled) { transform: translateY(0); }
    .login-submit-btn:disabled { opacity: 0.7; cursor: not-allowed; }
    .login-back-btn {
      width: 100%;
      padding: 12px;
      background: transparent;
      border: 1px solid #374151;
      border-radius: 12px;
      color: #9ca3af;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      transition: border-color 0.2s, color 0.2s;
      font-family: inherit;
      margin-top: 10px;
    }
    .login-back-btn:hover { border-color: #6366f1; color: #a5b4fc; }
    .login-spinner {
      width: 20px; height: 20px;
      border: 2px solid rgba(255,255,255,0.3);
      border-top-color: white;
      border-radius: 50%;
      animation: loginSpin 0.8s linear infinite;
    }
    .login-right-panel { animation: loginFadeIn 0.4s ease-out; }
    .login-checkbox-box {
      width: 18px; height: 18px;
      flex-shrink: 0;
      border-radius: 5px;
      cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      transition: all 0.2s;
    }
    .totp-dots {
      display: flex; justify-content: center; gap: 10px; margin-bottom: 8px;
    }
    .totp-dot {
      width: 12px; height: 12px;
      border-radius: 50%;
      background: #252837;
      border: 2px solid #374151;
      transition: background 0.15s, border-color 0.15s;
    }
    .totp-dot.filled { background: #6366f1; border-color: #6366f1; }
    @media (max-width: 767px) {
      .login-left-panel { display: none !important; }
      .login-right-panel { min-height: 100vh; }
    }
  `;

  // ── Left panel (shared) ────────────────────────────────────────────────────

  const LeftPanel = () => (
    <div
      className="login-left-panel"
      style={{
        width: '50%',
        background: 'linear-gradient(145deg, #3730a3 0%, #4f46e5 30%, #6d28d9 65%, #7c3aed 100%)',
        display: 'flex', flexDirection: 'column', padding: '44px 52px',
        position: 'relative', overflow: 'hidden',
      }}
    >
      <div style={{ position: 'absolute', top: '-100px', right: '-100px', width: '420px', height: '420px', background: 'rgba(167,139,250,0.22)', borderRadius: '50%', filter: 'blur(90px)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: '-80px', left: '-80px', width: '320px', height: '320px', background: 'rgba(99,102,241,0.18)', borderRadius: '50%', filter: 'blur(75px)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', top: '45%', left: '20%', width: '220px', height: '220px', background: 'rgba(139,92,246,0.12)', borderRadius: '50%', filter: 'blur(55px)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: 'repeating-linear-gradient(45deg, white 0px, white 1px, transparent 1px, transparent 40px)', pointerEvents: 'none' }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative', zIndex: 1 }}>
        <div style={{ width: '42px', height: '42px', background: 'rgba(255,255,255,0.15)', borderRadius: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)' }}>
          <img src="/logopanel.svg" alt="StreamCP Logo" style={{ width: '26px', height: '26px', filter: 'brightness(0) invert(1)' }} />
        </div>
        <span style={{ color: 'white', fontWeight: '700', fontSize: '18px', letterSpacing: '-0.01em' }}>StreamCP Pro</span>
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', position: 'relative', zIndex: 1, paddingRight: '16px' }}>
        <p style={{ color: 'rgba(255,255,255,0.72)', fontSize: '15px', marginBottom: '18px', fontWeight: '400', lineHeight: '1.65' }}>
          {step === 'totp'
            ? 'Segundo factor de autenticación'
            : isFirstSetup
              ? 'Configura tu panel de control\nde streaming profesional'
              : 'Desbloquea todo el potencial\nde tus transmisiones 24/7'}
        </p>
        <h2 style={{ color: 'white', fontSize: '30px', fontWeight: '700', lineHeight: '1.35', whiteSpace: 'pre-line' }}>
          {step === 'totp'
            ? 'Introduce el código\nde tu app\nautenticadora.'
            : isFirstSetup
              ? 'Crea tu cuenta de\nadministrador para\ncomenzar.'
              : 'Me alegra verte de nuevo.\nIntroduce tu correo\nelectrónico y contraseña\npara iniciar sesión.'}
        </h2>
      </div>
    </div>
  );

  // ── 2FA step UI ────────────────────────────────────────────────────────────

  if (step === 'totp') {
    return (
      <>
        <style>{styles}</style>
        <div style={{ display: 'flex', minHeight: '100vh', fontFamily: "'Figtree', sans-serif", background: '#0f1117' }}>
          <LeftPanel />
          <div className="login-right-panel" style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 32px', background: '#0f1117' }}>
            <div style={{ width: '100%', maxWidth: '400px' }}>

              {/* Header */}
              <div style={{ marginBottom: '32px', textAlign: 'center' }}>
                <div style={{ width: '64px', height: '64px', background: 'linear-gradient(135deg, #6366f1, #7c3aed)', borderRadius: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                  <ShieldCheck size={30} color="white" />
                </div>
                <h1 style={{ color: 'white', fontSize: '24px', fontWeight: '700', marginBottom: '8px', letterSpacing: '-0.02em' }}>
                  Verificación en 2 pasos
                </h1>
                <p style={{ color: '#6b7280', fontSize: '14px', lineHeight: '1.5' }}>
                  Abre tu app autenticadora e introduce<br />el código de 6 dígitos
                </p>
              </div>

              {/* Error */}
              {error && (
                <div style={{ marginBottom: '20px', padding: '12px 16px', borderRadius: '10px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', display: 'flex', alignItems: 'center', gap: '8px', color: '#f87171' }}>
                  <AlertCircle size={16} style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: '14px' }}>{error}</span>
                </div>
              )}

              {/* Progress dots */}
              <div className="totp-dots" style={{ marginBottom: '20px' }}>
                {[0, 1, 2, 3, 4, 5].map(i => (
                  <div key={i} className={`totp-dot${i < totpCode.length ? ' filled' : ''}`} />
                ))}
              </div>

              {/* TOTP form */}
              <form onSubmit={handleTotpSubmit}>
                <div style={{ position: 'relative', marginBottom: '20px' }}>
                  <KeyRound size={20} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#6366f1', pointerEvents: 'none', zIndex: 1 }} />
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={totpCode}
                    onChange={(e) => handleTotpInput(e.target.value)}
                    placeholder="000000"
                    autoFocus
                    autoComplete="one-time-code"
                    className="totp-input"
                    style={{ paddingLeft: '48px' }}
                  />
                </div>

                <button type="submit" disabled={loading || totpCode.length !== 6} className="login-submit-btn">
                  {loading ? (
                    <div className="login-spinner" />
                  ) : (
                    <>
                      <ShieldCheck size={18} />
                      Verificar código
                    </>
                  )}
                </button>

                <button type="button" onClick={handleBackToCredentials} className="login-back-btn">
                  ← Volver al inicio de sesión
                </button>
              </form>

              <div style={{ marginTop: '28px', textAlign: 'center' }}>
                <p style={{ fontSize: '13px', background: 'linear-gradient(135deg, #6366f1, #a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', fontWeight: '500' }}>
                  Potenciado por StreamCP Pro
                </p>
              </div>

            </div>
          </div>
        </div>
      </>
    );
  }

  // ── Step 1: credentials ────────────────────────────────────────────────────

  return (
    <>
      <style>{styles}</style>
      <div style={{ display: 'flex', minHeight: '100vh', fontFamily: "'Figtree', sans-serif", background: '#0f1117' }}>

        <LeftPanel />

        {/* ── RIGHT PANEL ── */}
        <div
          className="login-right-panel"
          style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 32px', background: '#0f1117' }}
        >
          <div style={{ width: '100%', maxWidth: '400px' }}>

            <h1 style={{ color: 'white', fontSize: '26px', fontWeight: '700', marginBottom: '28px', letterSpacing: '-0.02em' }}>
              {isFirstSetup ? 'Crear cuenta' : 'Iniciar sesión'}
            </h1>

            {error && (
              <div style={{ marginBottom: '20px', padding: '12px 16px', borderRadius: '10px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', display: 'flex', alignItems: 'center', gap: '8px', color: '#f87171' }}>
                <AlertCircle size={16} style={{ flexShrink: 0 }} />
                <span style={{ fontSize: '14px' }}>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit}>

              {/* Email */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', color: '#e5e7eb', fontSize: '13px', fontWeight: '600', marginBottom: '7px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Correo Electrónico
                </label>
                <div style={{ position: 'relative' }}>
                  <Mail size={17} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#6366f1', pointerEvents: 'none' }} />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@email.com"
                    required
                    className="login-input"
                    autoComplete="email"
                  />
                </div>
              </div>

              {/* Confirm Email (first setup only) */}
              {isFirstSetup && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: '#e5e7eb', fontSize: '13px', fontWeight: '600', marginBottom: '7px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Confirmar Correo
                  </label>
                  <div style={{ position: 'relative' }}>
                    <Mail size={17} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#6366f1', pointerEvents: 'none' }} />
                    <input
                      type="email"
                      value={emailConfirm}
                      onChange={(e) => setEmailConfirm(e.target.value)}
                      placeholder="tu@email.com"
                      required
                      className="login-input"
                      autoComplete="email"
                    />
                  </div>
                </div>
              )}

              {/* Password */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', color: '#e5e7eb', fontSize: '13px', fontWeight: '600', marginBottom: '7px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Contraseña
                </label>
                <div style={{ position: 'relative' }}>
                  <Lock size={17} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#6366f1', pointerEvents: 'none' }} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Tu contraseña"
                    required
                    className="login-input login-input-pass"
                    autoComplete={isFirstSetup ? 'new-password' : 'current-password'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ position: 'absolute', right: '13px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '4px', display: 'flex', alignItems: 'center' }}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </div>

              {/* Remember me */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                  <div
                    onClick={() => setRememberMe(!rememberMe)}
                    className="login-checkbox-box"
                    style={{ border: rememberMe ? '2px solid #7c3aed' : '2px solid #374151', background: rememberMe ? 'linear-gradient(135deg, #6366f1, #7c3aed)' : 'transparent' }}
                  >
                    {rememberMe && (
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                        <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                  <span style={{ color: '#9ca3af', fontSize: '14px', userSelect: 'none' }}>Recordarme</span>
                </label>
              </div>

              {/* Submit */}
              <button type="submit" disabled={loading} className="login-submit-btn">
                {loading ? (
                  <div className="login-spinner" />
                ) : (
                  <>
                    {isFirstSetup ? <UserPlus size={18} /> : <LogIn size={18} />}
                    {isFirstSetup ? 'Crear Cuenta' : 'Iniciar Sesión'}
                  </>
                )}
              </button>
            </form>

            <div style={{ marginTop: '32px', textAlign: 'center' }}>
              <p style={{ fontSize: '13px', background: 'linear-gradient(135deg, #6366f1, #a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', fontWeight: '500' }}>
                Potenciado por StreamCP Pro
              </p>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}

export default Login;
